import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(true);
  const [onboardingChecklist, setOnboardingChecklist] = useState({
    hasTitle: false,
    hasLocation: false,
    hasRate: false,
    hasSpecialties: false,
    hasPortfolio: false,
    hasAvailability: false
  });

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const config = { withCredentials: true, headers };

    // Fetch stats and bookings independently so one failure doesn't blank the other
    try {
      const statsRes = await axios.get(`${API}/stats/${user.role}`, config);
      setStats(statsRes.data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    }

    let bookingsData = [];
    try {
      const bookingsRes = await axios.get(`${API}/bookings`, config);
      bookingsData = bookingsRes.data;
      setBookings(bookingsData.slice(0, 5));
    } catch (e) {
      console.error('Error fetching bookings:', e);
    }

    try {
      const actions = [];
      bookingsData.forEach(booking => {
        if (user.role === 'business') {
          if (booking.status === 'pending' && !booking.agreement_id) {
            actions.push({ type: 'create_agreement', booking, message: `Create agreement for "${booking.project_title}"`, link: `/bookings/${booking.booking_id}/agreement`, priority: 'high' });
          } else if (booking.agreement_id && booking.payment_status === 'unpaid') {
            actions.push({ type: 'sign_or_pay', booking, message: `Complete agreement for "${booking.project_title}"`, link: `/bookings/${booking.booking_id}/agreement`, priority: 'high' });
          } else if (booking.status === 'deliverables_pending') {
            actions.push({ type: 'review_deliverables', booking, message: `Review deliverables for "${booking.project_title}"`, link: `/deliverables/${booking.booking_id}`, priority: 'high' });
          } else if (booking.status === 'completed' && !booking.reviewed) {
            actions.push({ type: 'leave_review', booking, message: `Leave a review for "${booking.project_title}"`, link: `/bookings?tab=completed`, priority: 'low' });
          }
        } else {
          // Only show "Sign agreement" if the creative hasn't signed yet
          if (booking.agreement_id && booking.status === 'agreement_pending' && !booking.agreement?.creative_signed) {
            actions.push({ type: 'sign_agreement', booking, message: `Sign agreement for "${booking.project_title}"`, link: `/bookings/${booking.booking_id}/agreement`, priority: 'high' });
          } else if (booking.status === 'in_progress') {
            actions.push({ type: 'upload_deliverables', booking, message: `Upload deliverables for "${booking.project_title}"`, link: `/deliverables/${booking.booking_id}`, priority: 'high' });
          }
        }
      });
      setPendingActions(actions);
    } catch (e) {
      console.error('Error building pending actions:', e);
    }

    if (user.role === 'creative') {
      try {
        const profileRes = await axios.get(`${API}/creatives/user/${user.user_id}`, config);
        const profile = profileRes.data;
        const checklist = {
          hasTitle: !!profile.title && profile.title.length > 0,
          hasLocation: !!profile.city && profile.city.length > 0,
          hasRate: profile.hourly_rate > 0,
          hasSpecialties: profile.specialties?.length > 0,
          hasPortfolio: profile.portfolio_items?.length > 0,
          hasAvailability: (profile.recurring_unavailable_days?.length > 0) || (profile.availability?.length > 0) || profile.availability_set === true
        };
        setOnboardingChecklist(checklist);
        setProfileComplete(checklist.hasTitle && checklist.hasLocation && checklist.hasRate && checklist.hasSpecialties);
      } catch (e) {
        setProfileComplete(false);
      }
    }

    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const map = {
      'pending':              { label: 'Pending',             cls: 'bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]' },
      'agreement_pending':    { label: 'Awaiting Acknowledgements', cls: 'bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa]' },
      'confirmed':            { label: 'Confirmed',           cls: 'bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]' },
      'in_progress':          { label: 'In Progress',         cls: 'bg-[#f5ead0] text-[#8a6a1a] border border-[#e8d49a]' },
      'deliverables_pending': { label: 'Review Files',        cls: 'bg-[#fdf4ff] text-[#7e22ce] border border-[#e9d5ff]' },
      'completed':            { label: 'Completed',           cls: 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]' },
      'cancelled':            { label: 'Cancelled',           cls: 'bg-[#fef2f2] text-[#991b1b] border border-[#fecaca]' },
    };
    return map[status] || { label: status, cls: 'bg-[#f3f4f6] text-[#374151] border border-[#e5e7eb]' };
  };

  const completedCount = Object.values(onboardingChecklist).filter(Boolean).length;
  const completionPct = (completedCount / 6) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      {/* ── Page Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-3 px-5 md:px-10 pt-7 pb-0">
        <div>
          <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-1.5">Dashboard</p>
          <h1 className="font-display font-normal text-[28px] md:text-[32px] text-[#0e0e0e] tracking-[-0.5px] leading-[1.1]">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[13px] text-[#a0a0a0] mt-1">
            {user?.role === 'creative' ? 'Manage your profile and bookings' : 'Find and hire amazing creatives'}
          </p>
        </div>
        <div className="flex gap-2.5 items-center mt-1">
          {user?.role === 'business' ? (
            <button
              onClick={() => navigate('/projects', { state: { openCreateDialog: true } })}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium px-5 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all"
            >
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 4v12M4 10h12" strokeLinecap="round"/></svg>
              Post Project
            </button>
          ) : (
            <Link
              to="/edit-profile"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium px-5 py-2 rounded-full border border-[#e4e1db] bg-white text-[#0e0e0e] hover:border-[#0e0e0e] transition-all"
            >
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="10" cy="7" r="4"/><path d="M2 18c0-3.5 3.6-6 8-6s8 2.5 8 6" strokeLinecap="round"/></svg>
              Edit Profile
            </Link>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="px-5 md:px-10 pt-7 pb-10 space-y-6">

        {/* Profile Completion Banner — creative only */}
        {user?.role === 'creative' && !profileComplete && (
          <div className="relative overflow-hidden bg-white border border-[#e4e1db] rounded-[24px] p-6 flex items-start gap-4">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#d4a843] rounded-l-[24px]" />
            <div className="w-10 h-10 rounded-full bg-[#f5ead0] border border-[#e8d49a] flex items-center justify-center text-lg flex-shrink-0">✦</div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-[#0e0e0e] mb-0.5">Complete your profile to get discovered</p>
              <p className="text-[13px] text-[#5a5a5a] mb-4">Finish setting up your profile to appear in search results and start receiving bookings.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {[
                  { key: 'hasTitle',        label: 'Add your title' },
                  { key: 'hasLocation',     label: 'Set your location' },
                  { key: 'hasRate',         label: 'Set hourly rate' },
                  { key: 'hasSpecialties',  label: 'Add specialties' },
                  { key: 'hasPortfolio',    label: 'Add portfolio items' },
                  { key: 'hasAvailability', label: 'Set availability' },
                ].map(item => (
                  <div key={item.key} className="flex items-center gap-2 text-[13px] text-[#5a5a5a]">
                    <div className={`w-[18px] h-[18px] rounded-full border flex-shrink-0 flex items-center justify-center ${onboardingChecklist[item.key] ? 'bg-[#0e0e0e] border-[#0e0e0e]' : 'border-[#e4e1db]'}`}>
                      {onboardingChecklist[item.key] && (
                        <svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 3l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      )}
                    </div>
                    {item.label}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-1 bg-[#f7f5f2] rounded-full overflow-hidden border border-[#e4e1db]">
                  <div className="h-full bg-[#0e0e0e] rounded-full transition-all duration-400" style={{ width: `${completionPct}%` }} />
                </div>
                <span className="text-[11px] font-semibold text-[#a0a0a0] whitespace-nowrap">{completedCount} / 6 complete</span>
                <Link to="/edit-profile" className="inline-flex items-center text-[12px] font-medium px-4 py-1.5 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity">
                  Complete Profile →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Pending Actions */}
        {pendingActions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3.5">
              <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Action Required</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0e0e0e] text-white">{pendingActions.length}</span>
            </div>
            <div className="space-y-2">
              {pendingActions.slice(0, 3).map((action, idx) => (
                <Link key={idx} to={action.link} className="flex items-center justify-between p-4 bg-white border border-[#e4e1db] rounded-[14px] hover:border-[#0e0e0e] transition-colors cursor-pointer">
                  <div className="flex items-center gap-3.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${action.priority === 'high' ? 'bg-[#0e0e0e]' : 'bg-[#f7f5f2] border border-[#e4e1db]'}`}>
                      <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke={action.priority === 'high' ? 'white' : '#5a5a5a'} strokeWidth="1.5" strokeLinecap="round"><path d="M6 2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"/><path d="M8 7h4M8 10h4M8 13h2"/></svg>
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-[#0e0e0e]">{action.message}</p>
                      <p className="text-[11px] text-[#a0a0a0] mt-0.5">${action.booking.total_amount?.toFixed(2)} · {action.booking.date}</p>
                    </div>
                  </div>
                  <span className="text-[#a0a0a0] text-base">→</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {user?.role === 'creative' ? (
            <>
              <StatCard icon={<DollarIcon />} value={`$${parseFloat(stats?.total_earnings || 0).toFixed(2)}`} label="Total Earnings" />
              <StatCard icon={<EscrowIcon />} value={`$${parseFloat(stats?.pending_earnings || 0).toFixed(2)}`} label="In Escrow" />
              <StatCard icon={<CalendarIcon />} value={stats?.total_bookings || 0} label="Completed Jobs" />
              <StatCard icon={<StarIcon />} value={stats?.rating ? parseFloat(stats.rating).toFixed(1) : '—'} label={`Rating (${stats?.total_reviews || 0})`} />
            </>
          ) : (
            <>
              <StatCard icon={<DollarIcon />} value={`$${parseFloat(stats?.total_spent || 0).toFixed(2)}`} label="Total Spent" />
              <StatCard icon={<CalendarIcon />} value={stats?.total_bookings || 0} label="Total Bookings" />
              <StatCard icon={<EscrowIcon />} value={stats?.active_bookings || 0} label="Active" />
              <StatCard icon={<BriefcaseIcon />} value={stats?.open_projects || 0} label="Open Projects" />
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {user?.role === 'business' ? (
            <>
              <Link to="/photographers" className="flex items-center justify-between p-[18px_20px] bg-white border border-[#e4e1db] rounded-[14px] hover:border-[#0e0e0e] hover:shadow-[0_2px_12px_rgba(14,14,14,0.06)] transition-all">
                <div className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-full bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5" strokeLinecap="round"><circle cx="10" cy="7" r="4"/><path d="M2 18c0-3.5 3.6-6 8-6s8 2.5 8 6"/></svg>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#0e0e0e]">Browse Photographers</p>
                    <p className="text-[11px] text-[#a0a0a0] mt-0.5">Find photographers and videographers</p>
                  </div>
                </div>
                <span className="text-[#a0a0a0] text-base">→</span>
              </Link>
              <button onClick={() => navigate('/projects', { state: { openCreateDialog: true } })} className="flex items-center justify-between p-[18px_20px] bg-[#0e0e0e] border border-[#0e0e0e] rounded-[14px] hover:opacity-90 transition-opacity text-left w-full">
                <div className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round"><path d="M10 4v12M4 10h12"/></svg>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-white">Post a Project</p>
                    <p className="text-[11px] text-white/45 mt-0.5">Let creatives come to you</p>
                  </div>
                </div>
                <span className="text-white/40 text-base">→</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/projects" className="flex items-center justify-between p-[18px_20px] bg-white border border-[#e4e1db] rounded-[14px] hover:border-[#0e0e0e] hover:shadow-[0_2px_12px_rgba(14,14,14,0.06)] transition-all">
                <div className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-full bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="3" width="16" height="15" rx="2"/><path d="M2 8h16M6 1v4M14 1v4"/></svg>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#0e0e0e]">Browse Projects</p>
                    <p className="text-[11px] text-[#a0a0a0] mt-0.5">Find work opportunities</p>
                  </div>
                </div>
                <span className="text-[#a0a0a0] text-base">→</span>
              </Link>
              <Link to="/availability" className="flex items-center justify-between p-[18px_20px] bg-[#0e0e0e] border border-[#0e0e0e] rounded-[14px] hover:opacity-90 transition-opacity">
                <div className="flex items-center gap-3.5">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="3" width="16" height="15" rx="2"/><path d="M2 8h16M6 1v4M14 1v4"/></svg>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-white">Set Availability</p>
                    <p className="text-[11px] text-white/45 mt-0.5">Manage your calendar</p>
                  </div>
                </div>
                <span className="text-white/40 text-base">→</span>
              </Link>
            </>
          )}
        </div>

        {/* Recent Bookings */}
        <div>
          <div className="flex items-center justify-between mb-3.5">
            <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Recent Bookings</p>
            <Link to="/bookings" className="text-[12px] font-medium text-[#5a5a5a] hover:text-[#0e0e0e] transition-colors">View all →</Link>
          </div>

          <div className="bg-white border border-[#e4e1db] rounded-[24px] overflow-hidden">
            {bookings.length > 0 ? (
              <div>
                {bookings.map((booking, i) => {
                  const badge = getStatusBadge(booking.status);
                  const initials = (user?.role === 'business' ? booking.creative_name : booking.business_name)
                    ?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
                  return (
                    <Link
                      key={booking.booking_id}
                      to={booking.agreement_id ? `/bookings/${booking.booking_id}/agreement` : '/bookings'}
                      className={`flex items-center justify-between px-5 py-4 hover:bg-[#f7f5f2] transition-colors ${i < bookings.length - 1 ? 'border-b border-[#e4e1db]' : ''}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-[38px] h-[38px] rounded-full bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center text-[14px] font-semibold text-[#0e0e0e] flex-shrink-0">
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-[#0e0e0e] truncate">{booking.project_title}</p>
                          <p className="text-[11px] text-[#a0a0a0] mt-0.5 truncate">
                            {user?.role === 'business' ? booking.creative_name : booking.business_name} · {booking.date}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-[13px] font-bold text-[#0e0e0e] hidden sm:block">${parseFloat(booking.total_amount || 0).toFixed(2)}</span>
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full tracking-[0.3px] ${badge.cls}`}>{badge.label}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
                <div className="opacity-25 mb-3.5">
                  <svg width="40" height="40" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1"><rect x="2" y="3" width="16" height="15" rx="2"/><path d="M2 8h16M6 1v4M14 1v4"/></svg>
                </div>
                <p className="text-[15px] font-semibold text-[#0e0e0e] mb-1.5">No bookings yet</p>
                <p className="text-[13px] text-[#5a5a5a] mb-5 max-w-[280px] leading-relaxed">
                  {user?.role === 'creative'
                    ? 'Complete your profile so businesses can find and book you through FrameFinder.'
                    : 'Browse creatives and make your first booking.'}
                </p>
                <Link
                  to={user?.role === 'business' ? '/photographers' : '/edit-profile'}
                  className="inline-flex items-center text-[13px] font-medium px-5 py-2 rounded-full bg-[#0e0e0e] text-white hover:opacity-85 transition-opacity"
                >
                  {user?.role === 'business' ? 'Find Creatives →' : 'Complete Profile →'}
                </Link>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

/* ── Reusable stat card ── */
const StatCard = ({ icon, value, label }) => (
  <div className="bg-white border border-[#e4e1db] rounded-[24px] p-[22px] hover:shadow-[0_4px_20px_rgba(14,14,14,0.07)] hover:-translate-y-px transition-all">
    <div className="w-9 h-9 rounded-[8px] bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center mb-4">
      {icon}
    </div>
    <p className="font-display text-[30px] text-[#0e0e0e] tracking-[-1px] leading-none">{value}</p>
    <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-[#a0a0a0] mt-1.5">{label}</p>
  </div>
);

const DollarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5">
    <circle cx="10" cy="10" r="8"/>
    <path d="M10 6v1.5m0 5V14m3-5.5c0-1.4-1.3-2.5-3-2.5s-3 1.1-3 2.5 1.3 2.5 3 2.5 3 1.1 3 2.5-1.3 2.5-3 2.5-3-1.1-3-2.5" strokeLinecap="round"/>
  </svg>
);
const EscrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5">
    <path d="M10 2a6 6 0 00-6 6v3.5L2.5 14h15L16 11.5V8a6 6 0 00-6-6z"/>
    <path d="M8 15a2 2 0 004 0" strokeLinecap="round"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5">
    <rect x="2" y="3" width="16" height="15" rx="2"/>
    <path d="M2 8h16M6 1v4M14 1v4" strokeLinecap="round"/>
  </svg>
);
const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5">
    <path d="M10 2l2 5.5H18l-4.5 3.5 1.7 5L10 13l-5.2 3 1.7-5L2 7.5h6L10 2z"/>
  </svg>
);
const BriefcaseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#5a5a5a" strokeWidth="1.5">
    <rect x="2" y="7" width="16" height="11" rx="2"/>
    <path d="M7 7V5a2 2 0 012-2h2a2 2 0 012 2v2" strokeLinecap="round"/>
  </svg>
);

export default Dashboard;
